/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import { useEffect, useRef, useState } from 'react';

export interface PerformanceMetrics {
  fps: number;
  frameTime: number;
  memoryUsed?: number;
  entityCount: number;
  drawCalls?: number;
}

export const usePerformanceMonitor = (enabled: boolean = true) => {
  const frameCountRef = useRef(0);
  const lastTimeRef = useRef(performance.now());
  const [metrics, setMetrics] = useState<PerformanceMetrics>({
    fps: 60,
    frameTime: 16.67,
    entityCount: 0
  });

  useEffect(() => {
    if (!enabled) return;

    let animationId: number;
    let frameCount = 0;
    let lastFpsUpdate = performance.now();

    const measurePerformance = (currentTime: number) => {
      frameCount++;
      const deltaTime = currentTime - lastTimeRef.current;

      if (currentTime - lastFpsUpdate >= 1000) {
        const fps = Math.round((frameCount * 1000) / (currentTime - lastFpsUpdate));
        const frameTime = Math.round((deltaTime / frameCount) * 100) / 100;

        const memoryUsed = (performance as any).memory?.usedJSHeapSize;
        const drawCalls = (window as any).THREE?.renderer?.info.render.calls;

        setMetrics({
          fps,
          frameTime,
          memoryUsed,
          entityCount: getEntityCount(),
          drawCalls
        });

        frameCount = 0;
        lastFpsUpdate = currentTime;
      }

      lastTimeRef.current = currentTime;
      animationId = requestAnimationFrame(measurePerformance);
    };

    const getEntityCount = (): number => {
      // Count various entities for performance tracking
      const gridElement = document.querySelector('[data-three=""]');
      if (!gridElement) return 0;

      const entities = gridElement.querySelectorAll('[data-entity]');
      return entities.length;
    };

    animationId = requestAnimationFrame(measurePerformance);

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [enabled]);

  return metrics;
};